import { FC, useState } from 'react';
import { FaHardDrive } from 'react-icons/fa6';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Grid2, LinearProgress, Typography } from '@mui/material';

import { AddMinerModal } from 'shared/HostedMinersCard/AddMinerModal';
import { UserInformationMissingModal } from 'shared/Modals';
import {
  activeMinersCountSelector,
  allMinersSelector,
  deadMinersCountSelector,
  totalMinersCountSelector,
} from 'store/slices/miners-slice';
import { productsSelector } from 'store/slices/products-slice';
import { userDataSelector } from 'store/slices/user-slice';
import { useMuiTheme } from 'themes/themeMuiProvider';

const PERCENT = 100;

export const HostedMinersCard: FC = () => {
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();

  const user = useSelector(userDataSelector);

  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [showMissingInfoModal, setShowMissingInfoModal] = useState(false);

  const miners = useSelector(allMinersSelector) ?? [];
  const activeMinersCount = useSelector(activeMinersCountSelector);
  const deadMinersCount = useSelector(deadMinersCountSelector);
  const totalMinersCount = useSelector(totalMinersCountSelector);
  const products = useSelector(productsSelector);

  // Fleet-wide counts from the API, never derived from the page in the store — the store
  // only ever holds one page of devices.
  const errors = deadMinersCount;
  const percent = (activeMinersCount / (totalMinersCount || miners.length || 1)) * PERCENT;

  const buttonClickHandler = () => {
    if (!products.length) {
      setShowComingSoonModal(true);
    } else if (!user?.isFullInfoProvided) {
      setShowMissingInfoModal(true);
    } else {
      navigate('/shop');
    }
  };

  const closeComingSoonModal = () => {
    setShowComingSoonModal(false);
  };

  const closeMissingInfoModal = () => {
    setShowMissingInfoModal(false);
  };

  return (
    <>
      <Box
        onClick={() => navigate('/hosted-miners')}
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          backgroundColor: 'white',
          borderRadius: '12px',
          flexDirection: 'column',
          p: 2,
          gap: '1.5rem',
          cursor: 'pointer',
        }}
      >
        <Grid2 container direction='column' justifyContent='space-between' height='100%' spacing={2}>
          <Grid2 size={12}>
            <Box display='flex' alignItems='center' gap={1}>
              <FaHardDrive size={16} />
              <Typography variant='h6'>Hosted Miners</Typography>
            </Box>
          </Grid2>

          <Grid2 size={12}>
            <Typography variant='body2' color={activeMinersCount ? 'text.primary' : 'text.disabled'}>
              {activeMinersCount} running
            </Typography>
            <LinearProgress
              variant='determinate'
              value={percent}
              sx={{
                height: 6,
                borderRadius: 1,
                backgroundColor: muiTheme.colors.grayLight2,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: muiTheme.colors.green,
                },
                mt: 1,
              }}
            />
          </Grid2>

          <Grid2 size={12}>
            <Box display='flex' justifyContent='space-between' minHeight='40px'>
              <Typography
                variant='body2'
                sx={{
                  backgroundColor: errors ? muiTheme.colors.redLight : muiTheme.colors.grayLight2,
                  color: errors ? muiTheme.colors.red : muiTheme.colors.grayDark,
                  px: 2,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.25rem',
                }}
              >
                Errors: {errors}
              </Typography>

              <Button
                onClick={e => {
                  e.stopPropagation();
                  buttonClickHandler?.();
                }}
                variant='contained'
                color='secondary'
              >
                Add Miner
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      {showComingSoonModal && <AddMinerModal onClose={closeComingSoonModal} />}
      {showMissingInfoModal && <UserInformationMissingModal isModalOpen onClose={closeMissingInfoModal} />}
    </>
  );
};
